/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Left vertical tab rail of the Settings modal. Pinned to Figma:
//
//   * Sidebar frame 3542:4412 — width 301px, fill #15171C,
//     paddingTop 24, paddingBottom 20, paddingLeft/Right 12,
//     itemSpacing 12 (vertical).
//   * Title row 3542:4413     — "Settings" text Lato Bold 20/28
//     #FFFFFF (variable accent.0). Container is a horizontal frame
//     with paddingLeft 4, paddingRight 4.
//   * Tabs frame 3542:4415    — Vertical instance, itemSpacing 4.
//   * Tab button 3542:4382    — Each 44px tall, padding 12h/8v,
//     6px radius, item spacing 8. Active fill = #F4F4F5 at 8%
//     opacity (RGBA `244 244 245 / 0.08`). Inactive label uses the
//     text-secondary token; active uses text-primary.
//   * Account card 3542:4416  — paddingTop 20, paddingLeft 8,
//     itemSpacing 8 (horizontal). 40×40 pill avatar with the
//     display name + subtitle stack; destructive 36×28 sign-out
//     button (radius 24, padding 8h/4v, fill destructive @ 12%).
//
// All chrome carries `dark:` overrides on top of `light-*` token
// classes so the modal renders correctly in both themes.

import {
	Bell,
	HardDrive,
	Headphones,
	Keyboard,
	LayoutGrid,
	KeyRound,
	Link2,
	Lock,
	LogOut,
	type LucideIcon,
	Monitor,
	Palette,
	ScanFace,
	ShieldCheck,
	Sliders,
	Trash2,
	UserRound,
} from "lucide-react-native";
import { useEffect, useRef, useState } from "react";
import { Pressable, Text, View } from "react-native";
import {
	SETTINGS_TAB_LABELS,
	SETTINGS_TAB_ORDER,
	type SettingsTabId,
} from "./types";

const TAB_ICON: Record<SettingsTabId, LucideIcon> = {
	account: UserRound,
	session: Monitor,
	appearance: Palette,
	notifications: Bell,
	preferences: Sliders,
	keyboard: Keyboard,
	sidebar: LayoutGrid,
	voiceVideo: Headphones,
	storage: HardDrive,
	securityPrivacy: Lock,
	encryption: ScanFace,
	linking: Link2,
	recovery: KeyRound,
	helpAbout: ShieldCheck,
};

export interface SettingsSidebarProps {
	readonly active: SettingsTabId;
	readonly onChange: (next: SettingsTabId) => void;
	readonly accountDisplayName: string;
	readonly accountSubtitle: string;
	readonly onSignOut?: () => void;
	/** Zero-residue full local wipe of THIS device. When provided, the account
	 *  "Log out" button opens a popover with a second "Erase this device" option;
	 *  otherwise it signs out directly. */
	readonly onEraseThisDevice?: () => void;
}

export function SettingsSidebar(props: SettingsSidebarProps): JSX.Element {
	const {
		active,
		onChange,
		accountDisplayName,
		accountSubtitle,
		onSignOut,
		onEraseThisDevice,
	} = props;

	// The account "Log out" button becomes a popover trigger when an erase action
	// is available (Log out = preserve-resume; Erase this device = full wipe).
	const [logoutMenuOpen, setLogoutMenuOpen] = useState(false);
	const logoutMenuRef = useRef<View>(null);
	useEffect(() => {
		if (!logoutMenuOpen || typeof window === "undefined") return;
		const onDocClick = (event: MouseEvent): void => {
			const node = logoutMenuRef.current as unknown as HTMLElement | null;
			const target = event.target as Node | null;
			if (node && target && node.contains(target)) return;
			setLogoutMenuOpen(false);
		};
		const t = setTimeout(() => window.addEventListener("click", onDocClick), 0);
		return () => {
			clearTimeout(t);
			window.removeEventListener("click", onDocClick);
		};
	}, [logoutMenuOpen]);

	return (
		<View
			// Sidebar surface — Figma fill #15171C (dark) /
			// design-system light-surface-secondary (#F5F5F7) in light.
			// padding: 24t / 20b / 12h, itemSpacing handled per-section.
			// Border (3542:4412 stroke #22242A 1px) — applied on the
			// right edge only since the modal clips the other three.
			className="
				flex flex-col justify-between
				w-[301px] shrink-0
				bg-light-surface-secondary dark:bg-[#15171C]
				border-r border-light-border-secondary dark:border-[#22242A]
				pl-3 pr-3 pt-6 pb-5
				overflow-y-auto
			"
			// Hide the native scrollbar while keeping the scroll behaviour.
			// `overflow-y-auto` above provides the actual scroll; the
			// inline style nukes the visible track + thumb on Firefox
			// (`scrollbarWidth`) and legacy Edge (`msOverflowStyle`).
			// WebKit/Chromium need the `::-webkit-scrollbar` pseudo-
			// element which RN-Web's `View.style` can't express; the
			// project's global stylesheet handles that via a
			// `.scrollbar-hide` class (or `overflow-y: overlay` on
			// modern WebKit). Cast through `unknown` because RN's
			// `ViewStyle` is stricter than the underlying DOM types.
			style={
				{
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				} as unknown as import('react-native').ViewStyle
			}
		>
			<View className="flex flex-col gap-3">
				<View className="px-1 pb-1">
					<Text
						// "Settings" title — Lato Bold 20/28 #FFFFFF (Figma
						// uses pure white via accent.0 variable). Light mode
						// flips to the primary text token.
						className="
							text-[20px] leading-[28px] font-bold
							text-light-text-primary dark:text-white
						"
					>
						Settings
					</Text>
				</View>
				{/*
					Tabs — gap is 4px per Figma instance 3542:4415's
					itemSpacing (NOT 12; the parent's 12 sits between
					Title/Tabs/Account, not between tab items).
				*/}
				<View className="flex flex-col gap-1">
					{SETTINGS_TAB_ORDER.map((tab) => {
						const Icon = TAB_ICON[tab];
						const isActive = active === tab;
						// Lucide icon takes a literal color — we toggle on
						// active state and accept that the muted hex maps
						// reasonably to both themes. Active state inherits
						// the dark-mode primary text color so it stays
						// readable against the active fill.
						const iconColor = isActive ? "#1C1C1E" : "#8E8E93";
						const iconColorDark = isActive ? "#F5F6F8" : "#B5BAC3";
						return (
							<Pressable
								key={tab}
								testID={`settings-tab-${tab}`}
								accessibilityRole="tab"
								accessibilityState={{ selected: isActive }}
								onPress={() => onChange(tab)}
								// Tab base — Figma node 3542:4382.
								// 44px height, 12h/8v padding, 6px radius,
								// 8px gap. Active fill = rgba(244,244,245,
								// 0.08) on dark; on light we use the
								// neutral tag-user token at 8%.
								className={`
									flex flex-row items-center gap-2
									h-11 px-3 py-2
									rounded-md
									transition-colors
									${
										isActive
											? "bg-black/[0.06] dark:bg-white/[0.08]"
											: "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
									}
								`}
							>
								{/*
									Render twice for theme-correct colors —
									Pressable doesn't fan out lucide colors
									via CSS variables. The `hidden dark:flex`
									pair selects the right one per theme.
								*/}
								<View className="flex dark:hidden">
									<Icon size={18} strokeWidth={1.75} color={iconColor} />
								</View>
								<View className="hidden dark:flex">
									<Icon size={18} strokeWidth={1.75} color={iconColorDark} />
								</View>
								<Text
									className={`
										flex-1 text-[16px] leading-[22px] font-medium
										${
											isActive
												? "text-light-text-primary dark:text-[#F5F6F8]"
												: "text-light-text-secondary dark:text-[#B5BAC3]"
										}
									`}
									numberOfLines={1}
								>
									{SETTINGS_TAB_LABELS[tab]}
								</Text>
							</Pressable>
						);
					})}
				</View>
			</View>

			{/*
				Account card — Figma 3542:4416. Avatar 40×40 (radius 9999
				= full pill), then display name + subtitle stack, then a
				36×28 destructive icon button for sign-out. itemSpacing
				8, paddingTop 20, paddingLeft 8. The divider above is
				owned by the sidebar's natural separation — Figma renders
				the card flush to the bottom edge and uses the gap to
				separate it from the tabs.
			*/}
			<View
				className="
					flex flex-row items-center gap-2
					pt-5 pl-2 pr-1
				"
			>
				<View
					// Avatar — 40×40 pill (radius 9999 in Figma).
					className="
						w-10 h-10 rounded-full
						bg-light-accent-sky dark:bg-[#537EFF]
						flex items-center justify-center
					"
				>
					<Text className="text-[16px] leading-[22px] font-semibold text-white">
						{accountDisplayName.charAt(0).toUpperCase() || "?"}
					</Text>
				</View>
				<View className="min-w-0 flex-1">
					<Text
						// Display name — Figma I3542:4417;587:1864 14/Lato 700.
						className="
							text-[14px] leading-[20px] font-bold
							text-light-text-primary dark:text-[#F5F6F8]
						"
						numberOfLines={1}
					>
						{accountDisplayName || "—"}
					</Text>
					<Text
						// Subtitle — 12/Lato 400 #B5BAC3.
						className="
							text-[12px] leading-[16px] font-normal
							text-light-text-secondary dark:text-[#B5BAC3]
						"
						numberOfLines={1}
					>
						{accountSubtitle}
					</Text>
				</View>
				{onSignOut !== undefined && (
					<View ref={logoutMenuRef} className="relative shrink-0">
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Log out"
							onPress={() => {
								// With an erase action available, reveal the choice
								// popover; otherwise sign out directly.
								if (onEraseThisDevice !== undefined) {
									setLogoutMenuOpen((v) => !v);
								} else {
									onSignOut();
								}
							}}
							// Destructive icon button — Figma 3542:4418.
							// 36×28, radius 24, padding 8h/4v, fill is the
							// destructive token at 12% (matches Figma's
							// component variant for the soft destructive
							// background).
							className="
								inline-flex items-center justify-center
								w-9 h-7 rounded-[24px]
								bg-[#FF5353]/[0.12]
								hover:bg-[#FF5353]/[0.2]
								transition-colors
							"
						>
							<LogOut size={16} strokeWidth={1.75} color="#FF5353" />
						</Pressable>
						{onEraseThisDevice !== undefined && logoutMenuOpen ? (
							<View
								style={{
									position: "absolute",
									right: 0,
									bottom: 36,
									zIndex: 1000,
									minWidth: 200,
									padding: 4,
									borderRadius: 16,
									borderWidth: 1,
									borderColor: "#2F333B",
									backgroundColor: "rgba(21, 23, 28, 0.92)",
									...({
										backdropFilter: "blur(24px) saturate(180%)",
										WebkitBackdropFilter: "blur(24px) saturate(180%)",
										boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
									} as object),
								}}
							>
								<Pressable
									accessibilityRole="button"
									accessibilityLabel="Log out"
									onPress={() => {
										setLogoutMenuOpen(false);
										onSignOut();
									}}
									className="flex flex-row items-center gap-3 h-9 px-3 rounded-[10px] hover:bg-white/[0.06]"
								>
									<LogOut size={20} strokeWidth={1.5} color="#FF5353" />
									<Text className="font-sans text-[14px] font-bold text-[#FF5353]">
										Log out
									</Text>
								</Pressable>
								<Pressable
									accessibilityRole="button"
									accessibilityLabel="Erase this device"
									onPress={() => {
										setLogoutMenuOpen(false);
										onEraseThisDevice();
									}}
									className="flex flex-row items-center gap-3 h-9 px-3 rounded-[10px] hover:bg-white/[0.06]"
								>
									<Trash2 size={20} strokeWidth={1.5} color="#FF5353" />
									<Text className="font-sans text-[14px] font-bold text-[#FF5353]">
										Erase this device
									</Text>
								</Pressable>
							</View>
						) : null}
					</View>
				)}
			</View>
		</View>
	);
}
